import { runningClientSide } from '@benzinga/utils';
import {
  captureEmailFromPageUrl,
  appendAdLightClassToBody,
  appendPaywallClassToBody,
  getReferrerPartnerName,
  injectRaptiveFooterAdScript,
  injectRaptiveScript,
  removeRaptiveFooterAdScript,
  isPaywallClassAppended,
  appendStagingClassToBody,
  appendSandboxClassToBody,
  detectAndRemoveEmailsFromUrl,
  appendFooterDisabledClassToBody,
  removeFooterDisabledClassFromBody,
} from './utils';
import { RaptiveAdPlacementType, Targeting } from './entities';
import { raptiveAdWrapperClassnames } from './constants';
import type { BaseAdSlotEvent } from '../shared';

export const RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS = 'raptive-ad-placement';

declare global {
  interface Window {
    raptiveAdManager?: RaptiveAdManager;
    googletag?: any;
  }
}

interface AdItemInfo {
  width: number;
  height: number;
  networkCodes: string[];
  adUnitName: string;
  uniqueId: string;
  iframeId: string;
  index: number;
}

export interface SlotRenderEvent extends BaseAdSlotEvent {
  gptSlotId: string;
  size: [number, number] | null;
}

export interface AdSlotMapping {
  gptSlotId: string;
  iframeId: string | null;
  placeholderId: string;
  placementType: RaptiveAdPlacementType | null;
}

interface GptSlotRenderEndedEvent {
  isEmpty: boolean;
  size: [number, number] | null;
  slot: {
    getSlotElementId: () => string;
  };
}

const PAGES_WHERE_ADS_ARE_DISABLED = ['/login', '/services', '/free-newsletters', '/about', '/contact'];

const PAGES_WHERE_SCRIPT_IS_DISABLED = [
  '/welcome',
  '/privacy-policy',
  '/terms-and-conditions',
  '/disclaimer',
  '/logout',
  '/sponsored',
];

class RaptiveAdManager {
  private INITIALIZED = false; // This means init() was called, otherwise ad script was injected directly by requestAdLoad()
  private IS_SCRIPT_INJECTING = false;
  private IS_SCRIPT_INJECTED = false;
  private IS_FOOTER_AD_SCRIPT_INJECTED = false;
  private DISABLED = false;
  private FOOTER_DISABLED = false;
  private READY = false;

  private targeting: Targeting = {};

  private wasCaptureEmailFromPageUrlMethodCalled = false;

  private debounceTimer: NodeJS.Timeout | null = null;

  private registeredPlaceholders: Set<string> = new Set();
  private expectedPlaceholderCount = 0;
  private adCounters: Record<string, number> = {};

  private observer: MutationObserver | null = null;

  private slotRenderCallbacks: Map<string, Array<(event: SlotRenderEvent) => void>> = new Map();
  private isGptEventListenerAttached = false;
  private renderedSlots: Map<string, SlotRenderEvent> = new Map();

  private placeholderToSlotMap: Map<string, AdSlotMapping> = new Map();
  private gptSlotToPlaceholderMap: Map<string, string> = new Map();

  constructor() {
    this.log('Initializing RaptiveAdManager', 'info');
    if (runningClientSide()) {
      window.raptiveAdManager = this;

      if (this.areAdsDisabledOnCurrentPage()) {
        this.log('Ads disabled on current page', 'warn');
      }

      if (this.isScriptDisabledOnCurrentPage()) {
        this.log('Ad script is disabled on this page', 'warn');
        this.disable();
        return;
      }
    }
  }

  public isLoaded(): boolean {
    return this.IS_SCRIPT_INJECTED;
  }

  public isDisabled(): boolean {
    return this.DISABLED;
  }

  public isReady(): boolean {
    return this.READY;
  }

  public getTargeting(): Targeting {
    return this.targeting;
  }

  public setTargeting(newTargeting: Targeting): Targeting {
    this.targeting = newTargeting;
    return this.targeting;
  }

  public setReady(isReady: boolean): boolean {
    if (this.READY === isReady) return this.READY;

    this.READY = isReady;
    if (isReady) {
      this.enable();
      this.initAdLoading();
    }
    return this.READY;
  }

  public disableFooter(): void {
    this.FOOTER_DISABLED = true;
    appendFooterDisabledClassToBody();
    this.removeFooterAdScript();
  }

  public enableFooter(): void {
    this.FOOTER_DISABLED = false;
    removeFooterDisabledClassFromBody();
  }

  public configure({
    footerDisabled,
    ready,
    targeting = {},
  }: {
    footerDisabled?: boolean;
    ready?: boolean;
    targeting?: Record<string, string | string[] | boolean>;
  } = {}): void {
    try {
      if (!runningClientSide()) return;

      const referrer = this.getReferrer();
      if (referrer) targeting.referrer = referrer;
      targeting.paywalled = this.getIsPaywalled();
      targeting && this.setTargeting(targeting);

      if (footerDisabled) {
        this.disableFooter();
      } else if (footerDisabled === false) {
        this.enableFooter();
      }

      if (typeof ready === 'boolean') {
        this.setReady(ready);
      }

      this.INITIALIZED = true;
    } catch (error) {
      this.log('Error initializing: ', 'error', error);
    }
  }

  public refresh(): void {
    this.IS_SCRIPT_INJECTED = false;
    this.loadAdScript();
    this.log('Ad manager refreshed', 'info');
  }

  public forceLoadAdScript(): void {
    this.log('Force loading ad script', 'info');
    this.loadAdScript(true);
  }

  public cleanup(): void {
    this.clearDebounceTimer();
    this.disconnectObserver();
  }

  public disable(): void {
    this.DISABLED = true;
    this.clearDebounceTimer();
    this.disableAllRaptiveAds();
  }

  public enable(): void {
    if (this.areAdsDisabledOnCurrentPage()) return;
    this.DISABLED = false;
  }

  public enableAdLightMode(): void {
    appendAdLightClassToBody();
  }

  public enablePaywallMode(): void {
    appendPaywallClassToBody();
  }

  public enableEnvironmentBodyClass(): void {
    appendStagingClassToBody();
    appendSandboxClassToBody();
  }

  public generateAdId(type: RaptiveAdPlacementType): string {
    if (!this.adCounters[type]) {
      this.adCounters[type] = 0;
    }
    this.adCounters[type]++;
    return `raptive-ad-${type}-${this.adCounters[type]}`;
  }

  public requestAdLoad(type: RaptiveAdPlacementType): string | null {
    if (this.DISABLED || !this.INITIALIZED) return null;
    const id = this.generateAdId(type);
    this.log(`Ad load requested: ${id} (type: ${type}, initialized: ${this.INITIALIZED})`, 'info');
    return id;
  }

  public getRegisteredPlaceholders(): string[] {
    return Array.from(this.registeredPlaceholders);
  }

  public getRegisteredPlaceholderCount(): number {
    return this.registeredPlaceholders.size;
  }

  public logAdElementsWithPlaceholderLengths(): void {
    if (!runningClientSide()) return;

    const adPlacements = this.getAdPlacementsInDOM();
    const adPlaceholderNames = this.getAdPlaceholderInDOM();

    this.log('Ad elements with placeholder lengths:', 'info');

    adPlacements.forEach((placement, index) => {
      const placeholderName = adPlaceholderNames[index] || 'unknown';
      const placeholderLength = placement.children.length;
      this.log(`  ${placeholderName}: ${placeholderLength} placeholder(s)`, 'info');
    });
  }

  public logPlacementsBeforeScriptInjection(): void {
    if (!runningClientSide()) return;

    const adPlacements = this.getAdPlacementsInDOM();

    if (adPlacements.length === 0) {
      this.log('No ad placements found in DOM before script injection', 'warn');
    } else if (this.expectedPlaceholderCount > 0 && adPlacements.length < this.expectedPlaceholderCount) {
      this.log(
        `Found fewer placements (${adPlacements.length}) than expected (${this.expectedPlaceholderCount})`,
        'warn',
      );
    }
  }

  public registerPlaceholder(type: RaptiveAdPlacementType): string | null {
    if (this.DISABLED) return null;

    // Allow registration even if not fully initialized to handle early component mounting
    const id = this.generateAdId(type);
    this.registeredPlaceholders.add(id);
    this.log(`Registered placeholder: ${id} (type: ${type}, initialized: ${this.INITIALIZED})`, 'info');
    return id;
  }

  public unregisterPlaceholder(id: string): void {
    if (this.registeredPlaceholders.has(id)) {
      this.registeredPlaceholders.delete(id);
      this.log(`Unregistered placeholder: ${id}`, 'info');

      if (runningClientSide() && window.googletag && window.googletag.apiReady) {
        window.googletag.cmd.push(() => {
          const pubads = window.googletag.pubads();
          const slots = pubads.getSlots();
          const slot = slots.find(s => s.getSlotElementId() === id);
          if (slot) {
            window.googletag.destroySlots([slot]);
            this.log(`Destroyed GPT slot for placeholder: ${id}`, 'info');
          }
        });
      }
    } else {
      this.log(`Attempted to unregister non-existent placeholder: ${id}`, 'warn');
    }
  }

  public setExpectedPlaceholderCount(count: number): void {
    this.expectedPlaceholderCount = count;
  }

  public setExpectedPlaceholderCountAndReady(count: number, isReady: boolean): void {
    if (this.expectedPlaceholderCount === count && this.READY === isReady) {
      return;
    }

    this.expectedPlaceholderCount = count;
    this.READY = isReady;
    this.log(`Updated state - count: ${count}, ready: ${isReady}`, 'info');

    if (isReady) {
      this.initAdLoading();
    }
  }

  public checkForRaptiveAdPlaceholders(): boolean {
    if (!runningClientSide()) return false;
    const adPlacements = this.getAdPlacementsInDOM();
    return adPlacements.length > 0;
  }

  public injectFooterAdScript({ disabled }: { disabled?: boolean } = {}): void {
    const isDisabled = Boolean(disabled) || this.FOOTER_DISABLED;
    if (isDisabled || this.DISABLED || this.IS_FOOTER_AD_SCRIPT_INJECTED) return;
    this.IS_FOOTER_AD_SCRIPT_INJECTED = true;
    injectRaptiveFooterAdScript();
  }

  public removeFooterAdScript(): void {
    this.IS_FOOTER_AD_SCRIPT_INJECTED = false;
    removeRaptiveFooterAdScript();
  }

  public captureEmailFromPageUrl({ disabled = false }): void {
    if (disabled || this.DISABLED || this.wasCaptureEmailFromPageUrlMethodCalled) return;
    this.wasCaptureEmailFromPageUrlMethodCalled = true;
    captureEmailFromPageUrl();
  }

  public areAdsDisabledOnCurrentPage(): boolean {
    if (!runningClientSide()) return false;
    // Required check for mobile app compatibility
    if (typeof window?.location?.pathname === 'undefined') return false;
    return PAGES_WHERE_ADS_ARE_DISABLED.includes(window.location.pathname);
  }

  public isScriptDisabledOnCurrentPage(): boolean {
    if (!runningClientSide()) return false;
    // Required check for mobile app compatibility
    if (typeof window?.location?.pathname === 'undefined') return false;
    return PAGES_WHERE_SCRIPT_IS_DISABLED.some(page => window.location.pathname.startsWith(page));
  }

  public findPlaceholderIdForSlot(gptSlotElementId: string): string | null {
    const cached = this.gptSlotToPlaceholderMap.get(gptSlotElementId);
    if (cached) return cached;

    if (!runningClientSide()) return null;

    const slotElement = document.getElementById(gptSlotElementId);
    if (!slotElement) return null;

    const placeholderWrapper = slotElement.closest(`.${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS}`);
    if (!placeholderWrapper) return null;

    const placeholderChild = placeholderWrapper.querySelector('[id^="raptive-ad-"]') as HTMLElement | null;
    if (!placeholderChild?.id) return null;

    const placeholderId = placeholderChild.id;
    const placementType = (placeholderChild.getAttribute('data-placement-type') as RaptiveAdPlacementType) || null;
    const iframe = slotElement.querySelector('iframe') as HTMLIFrameElement | null;

    this.createSlotMapping({
      gptSlotId: gptSlotElementId,
      iframeId: iframe?.id || null,
      placeholderId,
      placementType,
    });

    return placeholderId;
  }

  public createSlotMapping(mapping: AdSlotMapping): void {
    this.placeholderToSlotMap.set(mapping.placeholderId, mapping);
    this.gptSlotToPlaceholderMap.set(mapping.gptSlotId, mapping.placeholderId);
  }

  public getSlotMapping(placeholderId: string): AdSlotMapping | null {
    return this.placeholderToSlotMap.get(placeholderId) || null;
  }

  public getPlaceholderIdByGptSlot(gptSlotId: string): string | null {
    return this.gptSlotToPlaceholderMap.get(gptSlotId) || null;
  }

  public extractAdUrlFromDestination(rawUrl: string): string {
    try {
      const url = new URL(rawUrl);
      const adurl = url.searchParams.get('adurl');
      if (adurl) {
        return decodeURIComponent(adurl);
      }
    } catch {
      // Not a valid URL, return as-is
    }
    return rawUrl;
  }

  public hasValidAdIframe(adContainerId: string): boolean {
    if (!runningClientSide()) return false;

    try {
      const adContainer = document.getElementById(adContainerId);
      if (!adContainer) return false;

      const iframeContainer = adContainer.querySelector('[id*="google_ads_iframe_"][id$="__container__"]');
      if (!iframeContainer) return false;

      const iframe = iframeContainer.querySelector('iframe') as HTMLIFrameElement | null;
      if (!iframe) return false;

      const iframeWidth = iframe.width ? parseInt(iframe.width, 10) : iframe.offsetWidth;
      return iframeWidth > 1;
    } catch (error) {
      this.log(`Could not check iframe validity for ${adContainerId}: ${error}`, 'warn');
      return false;
    }
  }

  public getAdDestinationUrl(adContainerId: string): { destinationUrl: string | null; rawUrl: string | null } {
    if (!runningClientSide()) return { destinationUrl: null, rawUrl: null };

    try {
      const adContainer = document.getElementById(adContainerId);
      if (!adContainer) return { destinationUrl: null, rawUrl: null };

      const iframe = adContainer.querySelector('iframe') as HTMLIFrameElement | null;
      if (!iframe) return { destinationUrl: null, rawUrl: null };

      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDocument) return { destinationUrl: null, rawUrl: null };

      const anchor = iframeDocument.querySelector('a');
      const rawUrl = anchor?.href || null;

      if (!rawUrl) return { destinationUrl: null, rawUrl: null };

      const destinationUrl = this.extractAdUrlFromDestination(rawUrl);
      return { destinationUrl, rawUrl };
    } catch (error) {
      this.log(`Could not access ad destination URL for ${adContainerId}: ${error}`, 'warn');
      return { destinationUrl: null, rawUrl: null };
    }
  }

  public onSlotRendered(slotElementId: string, callback: (event: SlotRenderEvent) => void): () => void {
    if (!runningClientSide()) {
      return function noopCleanup() {
        /* no-op for SSR */
      };
    }

    this.setupGptEventListener();

    const existingEvent = this.renderedSlots.get(slotElementId);
    if (existingEvent) {
      callback(existingEvent);
      return function noopCleanup() {
        /* already resolved */
      };
    }

    const callbacks = this.slotRenderCallbacks.get(slotElementId) || [];
    callbacks.push(callback);
    this.slotRenderCallbacks.set(slotElementId, callbacks);

    return () => {
      const currentCallbacks = this.slotRenderCallbacks.get(slotElementId);
      if (currentCallbacks) {
        const index = currentCallbacks.indexOf(callback);
        if (index > -1) {
          currentCallbacks.splice(index, 1);
        }
        if (currentCallbacks.length === 0) {
          this.slotRenderCallbacks.delete(slotElementId);
        }
      }
    };
  }

  public observeAdLoad(
    adContainerId: string,
    callback: (event: SlotRenderEvent) => void,
    options: { timeout?: number } = {},
  ): () => void {
    if (!runningClientSide()) {
      return function noopCleanup() {
        /* no-op for SSR */
      };
    }

    const { timeout = 30000 } = options;
    let resolved = false;
    let timeoutId: NodeJS.Timeout | null = null;

    const wrappedCallback = (event: SlotRenderEvent) => {
      if (resolved) return;
      resolved = true;
      if (timeoutId) clearTimeout(timeoutId);

      if (!event.destinationUrl) {
        const urlResult = this.getAdDestinationUrl(adContainerId);
        event.destinationUrl = urlResult.destinationUrl;
        event.rawDestinationUrl = urlResult.rawUrl;
      }

      callback(event);
    };

    const unsubscribe = this.onSlotRendered(adContainerId, wrappedCallback);

    timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        unsubscribe();
        this.log(`Timeout waiting for slot ${adContainerId} to render`, 'warn');
        callback({
          destinationUrl: null,
          gptSlotId: '',
          isEmpty: true,
          placeholderId: adContainerId,
          rawDestinationUrl: null,
          size: null,
        });
      }
    }, timeout);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      unsubscribe();
    };
  }

  private setupGptEventListener(): void {
    if (this.isGptEventListenerAttached) return;

    this.isGptEventListenerAttached = true;

    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd = window.googletag.cmd || [];

    window.googletag.cmd.push(() => {
      window.googletag.pubads().addEventListener('slotRenderEnded', (gptEvent: GptSlotRenderEndedEvent) => {
        const slotElementId = gptEvent.slot.getSlotElementId();
        const gptIsEmpty = gptEvent.isEmpty;
        const size = gptEvent.size;
        const placeholderId = this.findPlaceholderIdForSlot(slotElementId);
        const adContainerId = placeholderId || slotElementId;

        const processSlotRender = (isEmpty: boolean) => {
          const urlResult = isEmpty ? { destinationUrl: null, rawUrl: null } : this.getAdDestinationUrl(adContainerId);

          const event: SlotRenderEvent = {
            destinationUrl: urlResult.destinationUrl,
            gptSlotId: slotElementId,
            isEmpty,
            placeholderId,
            rawDestinationUrl: urlResult.rawUrl,
            size,
          };

          this.renderedSlots.set(slotElementId, event);
          if (placeholderId) {
            this.renderedSlots.set(placeholderId, event);
          }

          const callbackKey = placeholderId || slotElementId;
          const callbacks = this.slotRenderCallbacks.get(callbackKey);
          if (callbacks && callbacks.length > 0) {
            callbacks.forEach(cb => cb(event));
            this.slotRenderCallbacks.delete(callbackKey);
          }
        };

        if (gptIsEmpty) {
          processSlotRender(true);
          return;
        }

        if (this.hasValidAdIframe(adContainerId)) {
          processSlotRender(false);
          return;
        }

        const adContainer = document.getElementById(adContainerId);
        if (!adContainer) {
          processSlotRender(true);
          return;
        }

        let resolved = false;
        const maxTimeout = 5000;

        const observer = new MutationObserver(() => {
          if (resolved) return;
          if (this.hasValidAdIframe(adContainerId)) {
            resolved = true;
            observer.disconnect();
            processSlotRender(false);
          }
        });

        observer.observe(adContainer, {
          attributes: true,
          childList: true,
          subtree: true,
        });

        setTimeout(() => {
          if (resolved) return;
          resolved = true;
          observer.disconnect();
          processSlotRender(!this.hasValidAdIframe(adContainerId));
        }, maxTimeout);
      });
    });
  }

  private initAdLoading(): void {
    if (!runningClientSide()) return;
    this.log(`Initializing ad loading - expected placeholders: ${this.expectedPlaceholderCount}`, 'info');

    // Fallback: If there are no expected counts, no registered placeholders, and no wrappers/children in DOM,
    // load the script so features like email capture and footer scripts can run on pages without ads.
    if (this.expectedPlaceholderCount === 0 && this.getRegisteredPlaceholderCount() === 0) {
      const selector = `.${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS}`;
      const hasAnyAdPlacementWrappers = Array.from(document.querySelectorAll(selector)).length > 0;
      if (!hasAnyAdPlacementWrappers) {
        this.log('No placeholders registered or present in DOM; loading ad script (fallback).', 'info');
        this.loadAdScript();
        return;
      }
    }

    if (this.checkIfAdPlaceholdersInDOMMeetsExpected()) {
      this.log('Expected placeholders found in DOM, loading ad script', 'info');
      this.loadAdScript();
      return;
    }

    this.log('Setting up MutationObserver to watch for ad placeholders', 'info');
    this.setupAdPlaceholderObserver();
  }

  private getAdPlacementsInDOM(): Element[] {
    const adPlacements = document.querySelectorAll(`.${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS} > *`);
    const adPlaceholders = Array.from(adPlacements) as HTMLElement[];

    this.log(`Found ${adPlaceholders.length} ad placeholders inside ${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS}`, 'info');

    const adPlaceholdersFormatted = adPlaceholders.map((element, index) => ({
      id: element.id,
      index,
      type: element.getAttribute('data-placement-type') || null,
    }));
    if (adPlaceholdersFormatted.length > 0) {
      this.log('Ad placeholders (id,type)', 'table', adPlaceholdersFormatted);
    }

    return adPlaceholders;
  }

  private getAdPlaceholderInDOM(): string[] {
    const adPlaceholders = this.getAdPlacementsInDOM();
    return adPlaceholders.map(node => {
      const element = node as HTMLElement;
      const id = element.id || 'unknown-id';
      const type = element.getAttribute('data-placement-type') || 'unknown';
      return `${id}:${type}`;
    });
  }

  private getPlacementsInDOM(): Array<{
    index: number;
    hasChild: boolean;
    childId?: string;
    type?: string | null;
    deviceType?: string | null;
  }> {
    if (!runningClientSide()) return [];
    const placements = Array.from(document.querySelectorAll(`.${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS}`));
    return placements.map((placement, index) => {
      const placementElement = placement as HTMLElement;
      const child = placementElement.firstElementChild as HTMLElement | null;
      const hasChild = !!child;
      const childId = child?.id || undefined;
      const type = child?.getAttribute('data-placement-type') || null;
      const deviceType = placementElement.getAttribute('data-device-type') || null;
      return {
        childId,
        deviceType,
        hasChild,
        index,
        type,
      };
    });
  }

  private logPlacementsStatuses(context: string): void {
    const placements = this.getPlacementsInDOM();
    const placementsWithoutChildren = placements
      .filter(placement => !placement.hasChild)
      .map(placement => placement.index);
    this.log(`${context} - placements statuses (pending indexes: [${placementsWithoutChildren.join(', ')}])`, 'info');
    if (placements.length > 0) {
      this.log('Placements debug table', 'table', placements);
    }
  }

  private getLoadedAdsInDOM(): Record<string, AdItemInfo> {
    if (!runningClientSide()) return {};
    const adSizes: Record<string, AdItemInfo> = {};
    const raptiveAdWrapperSelectors = Object.values(raptiveAdWrapperClassnames)
      .map(className => `.${className}`)
      .join(', ');
    const adContainers = document.querySelectorAll(raptiveAdWrapperSelectors);
    adContainers.forEach(adContainer => {
      const iframe: HTMLIFrameElement | null =
        adContainer.querySelector("iframe[data-load-complete='true']") ||
        adContainer.querySelector("iframe[aria-label='Advertisement']");
      if (iframe?.id) {
        const width = parseInt(iframe.width, 10) || iframe.offsetWidth;
        const height = parseInt(iframe.height, 10) || iframe.offsetHeight;
        if (width && height) {
          const idParts = iframe.id.replace('google_ads_iframe', '').split('/');
          if (idParts) {
            const adUnitName = idParts?.[2];
            adSizes[adUnitName] = {
              adUnitName,
              height,
              iframeId: iframe.id,
              index: parseInt(idParts?.[2], 10) || 0,
              networkCodes: idParts?.[1]?.split(','),
              uniqueId: idParts?.[3] || '',
              width,
            };
          }
        }
      }
    });
    return adSizes;
  }

  private getAdHeight(adId: string): { width: number; height: number } | null {
    if (!runningClientSide()) return null;

    const adContainer = document.getElementById(adId) as HTMLDivElement | null;
    if (!adContainer) return null;

    const iframe = adContainer.querySelector('iframe') as HTMLIFrameElement | null;
    if (!iframe) return null;

    return {
      height: parseInt(iframe.height),
      width: parseInt(iframe.width),
    };
  }

  private setupAdPlaceholderObserver(noDebounce?: false): void {
    if (!runningClientSide() || !this.READY || this.observer) return;

    const callback = () => {
      if (this.IS_SCRIPT_INJECTED) {
        this.disconnectObserver();
        return;
      }

      if (this.expectedPlaceholderCount > 0 && this.checkIfAdPlaceholdersInDOMMeetsExpected()) {
        const placeholderNames = this.getAdPlaceholderInDOM();
        this.log(
          `Expected placeholders met, loading ad script (${placeholderNames.length} found): ${placeholderNames.join(', ')}`,
          'info',
        );
        this.loadAdScript();
        this.disconnectObserver();
      } else if (this.expectedPlaceholderCount === 0) {
        if (this.checkIfAdPlaceholdersInDOMMeetsExpected()) {
          const placeholderNames = this.getAdPlaceholderInDOM();
          this.log(`All active wrappers filled, loading ad script (${placeholderNames.length} found)`, 'info');
          this.loadAdScript();
          this.disconnectObserver();
        }
      }
    };

    if (noDebounce) {
      callback();
      return;
    }

    let debounceTimeout: NodeJS.Timeout | null = null;
    const debouncedObserverCallback = () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      debounceTimeout = setTimeout(() => {
        callback();
      }, 50);
    };

    this.observer = new MutationObserver(mutations => {
      let hasRelevantChanges = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node instanceof HTMLElement) {
              const element = node as HTMLElement;
              if (
                element.classList.contains(RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS) ||
                element.querySelector(`.${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS}`) ||
                !!element.closest?.(`.${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS}`)
              ) {
                hasRelevantChanges = true;
                break;
              }
            }
          }

          if (hasRelevantChanges) {
            break;
          }
        } else if (mutation.type === 'attributes' && mutation.attributeName === 'id') {
          const target = mutation.target as HTMLElement;
          if (target.parentElement?.classList.contains(RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS)) {
            hasRelevantChanges = true;
            break;
          }
        }
      }

      if (hasRelevantChanges) {
        debouncedObserverCallback();
      }
    });

    const targetNode = document.body;
    this.observer.observe(targetNode, {
      attributeFilter: ['id'],
      attributes: true,
      childList: true,
      subtree: true,
    });
  }

  private getReasonForSkippingAdScriptInjection(): string[] {
    const reasons: string[] = [];

    if (this.DISABLED) {
      reasons.push('Raptive Ad Manager is disabled');
    }

    if (this.IS_SCRIPT_INJECTED) {
      reasons.push('Raptive ad script has already been loaded');
    }

    if (!this.READY) {
      reasons.push('Raptive Ad Manager is not ready');
    }

    if (this.expectedPlaceholderCount && !this.checkIfAdPlaceholdersInDOMMeetsExpected()) {
      reasons.push(
        `Expected (${this.expectedPlaceholderCount}) placeholders, but found (${this.getAdPlacementsInDOM().length}) placeholders`,
        `Registered placeholders (${this.getRegisteredPlaceholders().length}): ${this.getRegisteredPlaceholders().join(', ') || 'NONE'}`,
        `Placeholders in DOM (${this.getAdPlacementsInDOM().length}): ${this.getAdPlaceholderInDOM().join(', ') || 'NONE'}`,
      );
    }

    // Strict rule logging: if any wrapper exists without a child, wait for all to fill
    if (runningClientSide()) {
      const wrappers = Array.from(document.querySelectorAll(`.${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS}`));
      const filledCount = wrappers.filter(w => (w as HTMLElement).children.length > 0).length;
      if (wrappers.length > 0 && filledCount < wrappers.length) {
        const placementsWithoutChildren = this.getPlacementsInDOM()
          .filter(wrapper => !wrapper.hasChild)
          .map(wrapper => wrapper.index);
        reasons.push(
          `Detected ${wrappers.length} wrapper(s) but only ${filledCount} filled; waiting for ALL to fill. Pending indexes: [${placementsWithoutChildren.join(', ')}]`,
        );
      }
    }

    return reasons;
  }

  private isReadyToInjectAdScript(): boolean {
    const areExpectedAdsMissing = this.expectedPlaceholderCount && !this.checkIfAdPlaceholdersInDOMMeetsExpected();
    if (this.DISABLED || this.IS_SCRIPT_INJECTED || !this.READY || areExpectedAdsMissing) {
      const reasons = this.getReasonForSkippingAdScriptInjection();

      if (reasons.length > 0) {
        const reasonsList = reasons.join(', ');
        this.log(`Skipping ad script injection because: ${reasonsList}`, 'warn');
      } else {
        this.log('Skipping ad script injection', 'warn');
      }

      return false;
    }

    return true;
  }

  private executeLoadAdScript = (force?: boolean): void => {
    if (!runningClientSide()) return;

    const targeting = this.getTargeting();

    if (!force && !this.isReadyToInjectAdScript()) return;
    if (this.areAdsDisabledOnCurrentPage()) {
      this.disableFooter();
      this.disableAllRaptiveAds();
      this.log('Ads disabled on current page', 'warn');
    }

    if (this.isScriptDisabledOnCurrentPage()) {
      this.log('Ads are disabled on this page - skipping script injection', 'warn');
      return;
    }

    if (!this.IS_SCRIPT_INJECTING) {
      this.IS_SCRIPT_INJECTING = true;
      this.detectAndRemoveEmailsFromUrl();
      this.log('Injecting ad script', 'info');
      this.logPlacementsBeforeScriptInjection();
      injectRaptiveScript({
        onError: () => {
          this.IS_SCRIPT_INJECTING = false;
          this.clearDebounceTimer();
          this.log('Ad script injection failed', 'error');
        },
        onLoad: () => {
          this.log('Ad script injected successfully', 'info');
          this.IS_SCRIPT_INJECTED = true;
          this.IS_SCRIPT_INJECTING = false;
          this.clearDebounceTimer();

          this.injectFooterAdScript();
          this.captureEmailFromPageUrl({});
        },
        pageTargeting: { ...targeting, disableFooter: this.FOOTER_DISABLED },
      });
    } else {
      this.log('Ad script injection already in progress', 'info');
    }
  };

  private detectAndRemoveEmailsFromUrl = async () => {
    if (!runningClientSide()) return;
    if (!window.location.search) return;
    await detectAndRemoveEmailsFromUrl();
  };

  private checkIfAdPlaceholdersInDOMMeetsExpected(): boolean {
    if (!runningClientSide()) return false;

    const raptiveAdPlacements = Array.from(document.querySelectorAll(`.${RAPTIVE_AD_PLACEMENT_WRAPPER_CLASS}`));
    const isDesktop = window.innerWidth >= 768;

    const deviceBasedPlacements = raptiveAdPlacements.filter(placement => {
      const deviceType = (placement as HTMLElement).getAttribute('data-device-type');

      if (!deviceType || deviceType === 'all') return true;

      if (isDesktop) {
        return deviceType === 'desktop';
      } else {
        return deviceType === 'mobile';
      }
    });

    const deviceBasedPlacementsWithChildren = deviceBasedPlacements.filter(
      w => (w as HTMLElement).children.length > 0,
    ).length;

    this.log(
      `checkIfAdPlaceholdersInDOMMeetsExpected: placements=${raptiveAdPlacements.length}, active=${deviceBasedPlacements.length}, filled=${deviceBasedPlacementsWithChildren}, expected=${this.expectedPlaceholderCount}`,
      'info',
    );

    const result =
      deviceBasedPlacements.length > 0 && deviceBasedPlacementsWithChildren === deviceBasedPlacements.length;
    if (!result) {
      this.log(
        `Waiting for placements to fill before loading ad script (${deviceBasedPlacementsWithChildren}/${deviceBasedPlacements.length})`,
        'info',
      );
      this.logPlacementsStatuses('Waiting for placements to fill');
    }
    return result;
  }

  private getReferrer(): string | null {
    return getReferrerPartnerName();
  }

  private getIsPaywalled(): boolean {
    if (!runningClientSide()) return false;
    return isPaywallClassAppended();
  }

  private disconnectObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private clearDebounceTimer(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private loadAdScript = (force?: boolean): void => {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.executeLoadAdScript(force);
  };

  private disableAllRaptiveAds(): void {
    if (Array.isArray(window?.adthrive?.cmd)) {
      window.adthrive.cmd.push(function () {
        if (typeof window.adthrive.disableAds === 'function') {
          window.adthrive.disableAds();
        }
      });
    }
  }

  private log(
    message: string,
    method: 'log' | 'info' | 'table' | 'warn' | 'error' = 'log',
    ...optionalParams: unknown[]
  ): void {
    if (!runningClientSide()) return;

    if (
      typeof window.location !== 'undefined' &&
      new URLSearchParams(window.location.search).get('debug_raptive_ad_manager') !== 'true'
    )
      return;

    const prefix = '[Raptive Ad Manager] ';

    switch (method) {
      case 'table':
        console.group(`${prefix}${message}`);
        console.table(...optionalParams);
        console.groupEnd();
        break;
      case 'info':
        console.info(`${prefix}${message}`, ...optionalParams);
        break;
      case 'warn':
        console.warn(`${prefix}${message}`, ...optionalParams);
        break;
      case 'error':
        console.error(`${prefix}${message}`, ...optionalParams);
        break;
      case 'log':
      default:
        console.log(`${prefix}${message}`, ...optionalParams);
        break;
    }
  }
}

export const raptiveAdManager = new RaptiveAdManager();
